import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { InventoryService } from '../../services/inventoryService';
import { Inventory } from 'public-contracts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Package, Weight, Box, AlertCircle } from 'lucide-react';
import {
  WEIGHT_UNITS,
  VOLUME_UNITS,
  DEFAULT_WEIGHT_UNIT,
  DEFAULT_VOLUME_UNIT,
  getWeightUnitKeys,
  getVolumeUnitKeys,
  convertWeight,
  convertVolume,
  type WeightUnit,
  type VolumeUnit,
} from 'public-contracts';
import { useNotification } from '../../hooks/useNotification';

interface InventoryStockUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  inventory: Inventory | null;
  onSuccess?: () => void;
}

export const InventoryStockUpdateDialog: React.FC<
  InventoryStockUpdateDialogProps
> = ({ open, onClose, inventory, onSuccess }) => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState<number | undefined>();
  const [remainingItems, setRemainingItems] = useState<number | undefined>();
  const [remainingWeight, setRemainingWeight] = useState<number | undefined>();
  const [remainingVolume, setRemainingVolume] = useState<number | undefined>();

  // UI/input units (may differ from how the inventory is stored)
  const [weightUnit, setWeightUnit] = useState<WeightUnit>(DEFAULT_WEIGHT_UNIT);
  const [volumeUnit, setVolumeUnit] = useState<VolumeUnit>(DEFAULT_VOLUME_UNIT);

  useEffect(() => {
    if (open && inventory) {
      if (!inventory.isPackage) {
        // For non-package items, initialize with current quantity
        setQuantity(inventory.quantity);
      } else {
        // For package items, initialize with current remaining values
        setRemainingItems(
          inventory.remainingItems !== undefined &&
            inventory.remainingItems !== null
            ? inventory.remainingItems
            : undefined
        );
        setRemainingWeight(
          inventory.remainingWeight !== undefined &&
            inventory.remainingWeight !== null
            ? inventory.remainingWeight
            : undefined
        );
        setRemainingVolume(
          inventory.remainingVolume !== undefined &&
            inventory.remainingVolume !== null
            ? inventory.remainingVolume
            : undefined
        );
        setWeightUnit(
          (inventory.weightUnit as WeightUnit) || DEFAULT_WEIGHT_UNIT
        );
        setVolumeUnit(
          (inventory.volumeUnit as VolumeUnit) || DEFAULT_VOLUME_UNIT
        );
      }
    } else if (!open) {
      // Reset form when dialog closes
      setQuantity(undefined);
      setRemainingItems(undefined);
      setRemainingWeight(undefined);
      setRemainingVolume(undefined);
    }
  }, [open, inventory]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inventory) return;

    const storedWeightUnit: WeightUnit =
      inventory.weightUnit && inventory.weightUnit in WEIGHT_UNITS
        ? (inventory.weightUnit as WeightUnit)
        : DEFAULT_WEIGHT_UNIT;

    const storedVolumeUnit: VolumeUnit =
      inventory.volumeUnit && inventory.volumeUnit in VOLUME_UNITS
        ? (inventory.volumeUnit as VolumeUnit)
        : DEFAULT_VOLUME_UNIT;

    setLoading(true);
    try {
      console.log('🔍 Inventory stock update:', {
        inventoryId: inventory.id,
        isPackage: inventory.isPackage,
        quantityAnchor: inventory.quantityAnchor,
        quantity,
        hasRemainingItems: remainingItems !== undefined,
        hasRemainingWeight: remainingWeight !== undefined,
        hasRemainingVolume: remainingVolume !== undefined,
      });

      // Always use update-remaining endpoint for both package and non-package items
      if (!inventory.isPackage) {
        // For non-package items, update quantity directly
        if (quantity === undefined || quantity === null) {
          notify('Please enter quantity', { type: 'error' });
          setLoading(false);
          return;
        }

        if (quantity === inventory.quantity) {
          // No change
          notify('No quantity change', { type: 'info' });
          setLoading(false);
          return;
        }

        console.log(
          '📦 Calling updateRemainingValues (non-package) with quantity:',
          quantity
        );

        const result = await InventoryService.updateRemainingValues(
          inventory.id,
          {
            quantity,
          }
        );

        console.log('✅ Update successful, new quantity:', result.newQuantity);

        notify(
          `Stock updated successfully. New quantity: ${result.newQuantity}`,
          {
            type: 'success',
          }
        );
      } else {
        // For package items, use anchor-based remaining values
        const updates: {
          remainingItems?: number;
          remainingWeight?: number;
          remainingVolume?: number;
        } = {};

        const toStoredWeightUnit = (value: number) =>
          convertWeight(value, weightUnit, storedWeightUnit);

        const toStoredVolumeUnit = (value: number) =>
          convertVolume(value, volumeUnit, storedVolumeUnit);

        // When anchor is set, ONLY update the anchor field
        if (inventory.quantityAnchor === 'ITEM_COUNT') {
          if (remainingItems === undefined || remainingItems === null) {
            notify('Please enter remaining items', { type: 'error' });
            setLoading(false);
            return;
          }
          updates.remainingItems = remainingItems;
        } else if (inventory.quantityAnchor === 'WEIGHT') {
          if (remainingWeight === undefined || remainingWeight === null) {
            notify('Please enter remaining weight', { type: 'error' });
            setLoading(false);
            return;
          }
          // Convert from UI unit (e.g. g) to stored unit (e.g. kg)
          updates.remainingWeight = toStoredWeightUnit(remainingWeight);
        } else if (inventory.quantityAnchor === 'VOLUME') {
          if (remainingVolume === undefined || remainingVolume === null) {
            notify('Please enter remaining volume', { type: 'error' });
            setLoading(false);
            return;
          }
          // Convert from UI unit to stored unit
          updates.remainingVolume = toStoredVolumeUnit(remainingVolume);
        } else {
          // If no anchor is set, allow updating any value that was provided
          if (remainingItems !== undefined && remainingItems !== null) {
            updates.remainingItems = remainingItems;
          }
          if (remainingWeight !== undefined && remainingWeight !== null) {
            updates.remainingWeight = toStoredWeightUnit(remainingWeight);
          }
          if (remainingVolume !== undefined && remainingVolume !== null) {
            updates.remainingVolume = toStoredVolumeUnit(remainingVolume);
          }

          if (Object.keys(updates).length === 0) {
            notify('Please enter at least one remaining value', {
              type: 'error',
            });
            setLoading(false);
            return;
          }
        }

        console.log('📦 Calling updateRemainingValues (package) with:', {
          inventoryId: inventory.id,
          updates,
          storedWeightUnit,
          uiWeightUnit: weightUnit,
          storedVolumeUnit,
          uiVolumeUnit: volumeUnit,
        });

        const result = await InventoryService.updateRemainingValues(
          inventory.id,
          updates
        );

        console.log('✅ Update successful, new quantity:', result.newQuantity);

        notify(
          `Stock updated successfully. New quantity: ${result.newQuantity.toFixed(
            2
          )}`,
          { type: 'success' }
        );
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to update stock:', error);
      notify(
        error instanceof Error ? error.message : 'Failed to update stock',
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  if (!inventory) return null;

  // For non-package items, show quantity field
  // For package items, show anchor-based remaining values fields
  const isPackage = inventory.isPackage;

  // Show fields based on anchor - if anchor is set, ONLY show that field
  // If no anchor, show all relevant fields (only for packages)
  const showItemCount =
    isPackage &&
    inventory.itemCount !== undefined &&
    inventory.itemCount !== null &&
    (inventory.quantityAnchor === 'ITEM_COUNT' || !inventory.quantityAnchor);

  const showWeight =
    isPackage &&
    inventory.weight !== undefined &&
    inventory.weight !== null &&
    (inventory.quantityAnchor === 'WEIGHT' || !inventory.quantityAnchor);

  const showVolume =
    isPackage &&
    inventory.volume !== undefined &&
    inventory.volume !== null &&
    (inventory.quantityAnchor === 'VOLUME' || !inventory.quantityAnchor);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {translate('pos.inventory.fields.updateStock', 'Update Stock')}
          </DialogTitle>
          <DialogDescription>
            {inventory.name} ({inventory.code})
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isPackage && (
            <div className="space-y-2">
              <Label htmlFor="quantity">
                {translate('pos.inventory.fields.quantity', 'Quantity')}
              </Label>
              <Input
                id="quantity"
                type="number"
                min="0"
                step="0.01"
                placeholder="0"
                value={
                  quantity !== undefined && quantity !== null ? quantity : ''
                }
                onChange={(e) =>
                  setQuantity(
                    e.target.value ? parseFloat(e.target.value) : undefined
                  )
                }
                required
              />
              <p className="text-xs text-muted-foreground">
                {translate(
                  'pos.inventory.fields.currentQuantity',
                  `Current quantity: ${inventory.quantity}`,
                  {
                    quantity: inventory.quantity,
                  }
                )}
              </p>
            </div>
          )}

          {isPackage && inventory.quantityAnchor && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <AlertCircle className="h-4 w-4 mt-0.5 text-blue-600 dark:text-blue-400" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium">
                  {translate(
                    'pos.inventory.anchorActive',
                    'Quantity anchor is active'
                  )}
                </p>
                <p className="text-xs mt-1">
                  {translate(
                    'pos.inventory.anchorInfo',
                    `Quantity will be automatically calculated from ${inventory.quantityAnchor.toLowerCase()}`
                  )}
                </p>
              </div>
            </div>
          )}

          {showItemCount && (
            <div className="space-y-2">
              <Label htmlFor="remainingItems">
                {translate(
                  'pos.inventory.fields.remainingItems',
                  'Remaining Items'
                )}
              </Label>
              <Input
                id="remainingItems"
                type="number"
                min="0"
                placeholder="0"
                value={
                  remainingItems !== undefined && remainingItems !== null
                    ? remainingItems
                    : ''
                }
                onChange={(e) =>
                  setRemainingItems(
                    e.target.value ? parseInt(e.target.value, 10) : undefined
                  )
                }
              />
              {inventory.itemCount && inventory.itemCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.inventory.fields.remainingItemsHelp',
                    `Total remaining items. Quantity will be calculated as: remainingItems / ${inventory.itemCount}`
                  )}
                </p>
              )}
            </div>
          )}

          {showWeight && (
            <div className="space-y-2">
              <Label htmlFor="remainingWeight">
                {translate(
                  'pos.inventory.fields.remainingWeight',
                  'Remaining Weight'
                )}
              </Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Weight className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="remainingWeight"
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder="0.0"
                    value={
                      remainingWeight !== undefined && remainingWeight !== null
                        ? remainingWeight
                        : ''
                    }
                    onChange={(e) =>
                      setRemainingWeight(
                        e.target.value ? parseFloat(e.target.value) : undefined
                      )
                    }
                    className="pl-10"
                  />
                </div>
                <Select
                  value={weightUnit}
                  onValueChange={(value) => setWeightUnit(value as WeightUnit)}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getWeightUnitKeys().map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {WEIGHT_UNITS[unit].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {inventory.weight && inventory.weight > 0 && (
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.inventory.fields.remainingWeightHelp',
                    `Total remaining weight. Quantity will be calculated as: remainingWeight / ${inventory.weight}`
                  )}
                </p>
              )}
            </div>
          )}

          {showVolume && (
            <div className="space-y-2">
              <Label htmlFor="remainingVolume">
                {translate(
                  'pos.inventory.fields.remainingVolume',
                  'Remaining Volume'
                )}
              </Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Box className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="remainingVolume"
                    type="number"
                    min="0"
                    step="0.001"
                    placeholder="0.000"
                    value={
                      remainingVolume !== undefined && remainingVolume !== null
                        ? remainingVolume
                        : ''
                    }
                    onChange={(e) =>
                      setRemainingVolume(
                        e.target.value ? parseFloat(e.target.value) : undefined
                      )
                    }
                    className="pl-10"
                  />
                </div>
                <Select
                  value={volumeUnit}
                  onValueChange={(value) => setVolumeUnit(value as VolumeUnit)}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getVolumeUnitKeys().map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {VOLUME_UNITS[unit].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {inventory.volume && inventory.volume > 0 && (
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.inventory.fields.remainingVolumeHelp',
                    `Total remaining volume. Quantity will be calculated as: remainingVolume / ${inventory.volume}`
                  )}
                </p>
              )}
            </div>
          )}

          {isPackage && !showItemCount && !showWeight && !showVolume && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                {translate(
                  'pos.inventory.noFieldsAvailable',
                  'No fields available for this package item. Please set itemCount, weight, or volume in the inventory settings.'
                )}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {translate('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  {translate('common.updating', 'Updating...')}
                </>
              ) : (
                translate('common.update', 'Update')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
